/**
 * Admin Client Profiles Section - Unified Client Management System
 * Centralized view of all clients with services, documents, activity, and metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  Users, Search, Filter, Plus, RefreshCw, Loader2, ChevronRight,
  Building, Mail, Phone, Calendar, TrendingUp, Shield, Layers,
  AlertTriangle, CheckCircle, Clock, FileText, DollarSign,
  ArrowLeft, Eye, Edit, UserPlus, FolderOpen, Activity,
  BarChart3, Star, XCircle, ChevronDown, Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service badge colors
const SERVICE_COLORS = {
  Build: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-400' },
  Protect: { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-400' },
  Grow: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-400' },
  Basic: { bg: 'bg-gray-500/20', border: 'border-gray-500/40', text: 'text-gray-400' }
};

// Tier badge colors
const TIER_COLORS = {
  foundation: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Foundation' },
  accelerator: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Accelerator' },
  elite: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Elite' },
  basic: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Basic' },
  professional: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Professional' },
  enterprise: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Enterprise' },
  bronze: { bg: 'bg-orange-700/20', text: 'text-orange-400', label: 'Bronze' },
  silver: { bg: 'bg-gray-400/20', text: 'text-gray-300', label: 'Silver' },
  gold: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Gold' }
};

// Status badge colors
const STATUS_COLORS = {
  active: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
  trial: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Clock },
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
  suspended: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle }
};

const AdminClientProfilesSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_activity');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch clients');
      
      const data = await response.json();
      setClients(data.clients || []);
      setStats(data.stats || {});
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchable = `${client.first_name} ${client.last_name} ${client.business_name} ${client.email}`.toLowerCase();
        if (!searchable.includes(query)) return false;
      }
      
      // Service filter
      if (serviceFilter !== 'all') {
        const hasService = client.service_types?.includes(serviceFilter);
        if (!hasService) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        if (client.status !== statusFilter) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.business_name || '').localeCompare(b.business_name || '');
        case 'revenue':
          return (b.revenue || 0) - (a.revenue || 0);
        case 'days_active':
          return (b.days_active || 0) - (a.days_active || 0);
        case 'last_activity':
        default:
          return (b.last_activity || '').localeCompare(a.last_activity || '');
      }
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <AdminLayout title="Client Profiles" subtitle="Unified client management system">
      <div className="space-y-6" data-testid="admin-client-profiles">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard 
            label="Total Clients" 
            value={stats.total_clients || 0} 
            icon={Users}
            color="#D4AF37"
          />
          <StatCard 
            label="Active" 
            value={stats.active_clients || 0} 
            icon={CheckCircle}
            color="#22C55E"
          />
          <StatCard 
            label="Pending" 
            value={stats.pending_clients || 0} 
            icon={Clock}
            color="#F59E0B"
          />
          <StatCard 
            label="Build Clients" 
            value={stats.build_clients || 0} 
            icon={Layers}
            color="#8B5CF6"
          />
          <StatCard 
            label="Total Revenue" 
            value={formatCurrency(stats.total_revenue)} 
            icon={DollarSign}
            color="#22C55E"
          />
          <StatCard 
            label="Alerts" 
            value={stats.clients_with_alerts || 0} 
            icon={AlertTriangle}
            color="#EF4444"
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              placeholder="Search clients by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none transition-colors"
              data-testid="client-search-input"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="service-filter"
            >
              <option value="all">All Services</option>
              <option value="Build">Build</option>
              <option value="Protect">Protect</option>
              <option value="Grow">Grow</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="sort-filter"
            >
              <option value="last_activity">Last Activity</option>
              <option value="name">Company Name</option>
              <option value="revenue">Revenue</option>
              <option value="days_active">Days Active</option>
            </select>

            <button
              onClick={fetchClients}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-semibold hover:bg-[#C4A030] transition-colors"
              data-testid="create-client-btn"
            >
              <UserPlus className="w-4 h-4" />
              Add Client
            </button>
          </div>
        </div>

        {/* Client Table */}
        <div className="bg-white/5 border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-white/70 mb-4">{error}</p>
              <button 
                onClick={fetchClients}
                className="text-[#D4AF37] hover:underline"
              >
                Try Again
              </button>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Users className="w-12 h-12 text-white/20 mb-4" />
              <p className="text-white/50">No clients found</p>
              <p className="text-white/30 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Client</th>
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Services</th>
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Status</th>
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Phase</th>
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Revenue</th>
                    <th className="text-left px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Activity</th>
                    <th className="text-right px-4 py-3 text-white/50 text-xs uppercase tracking-wider font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <ClientRow 
                      key={client.id} 
                      client={client} 
                      onSelect={() => setSelectedClient(client)}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results count */}
        {!loading && !error && (
          <p className="text-white/40 text-sm">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
        )}

        {/* Create Client Modal */}
        {showCreateModal && (
          <CreateClientModal 
            onClose={() => setShowCreateModal(false)} 
            onSuccess={() => {
              setShowCreateModal(false);
              fetchClients();
            }}
          />
        )}

        {/* Client Detail Modal */}
        {selectedClient && (
          <ClientDetailModal 
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
            onRefresh={fetchClients}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white/5 border border-white/10 p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-white/50 text-sm">{label}</p>
  </div>
);

// Client Row Component
const ClientRow = ({ client, onSelect, formatDate, formatCurrency }) => {
  const StatusIcon = STATUS_COLORS[client.status]?.icon || Clock;
  
  return (
    <tr 
      className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
      onClick={onSelect}
      data-testid={`client-row-${client.id}`}
    >
      {/* Client Info */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
            {(client.business_name || client.first_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white font-semibold">{client.business_name || `${client.first_name} ${client.last_name}`}</p>
            <p className="text-white/40 text-sm">{client.email}</p>
          </div>
          {client.has_alerts && (
            <AlertTriangle className="w-4 h-4 text-red-400 ml-2" />
          )}
        </div>
      </td>

      {/* Services */}
      <td className="px-4 py-4">
        <div className="flex flex-wrap gap-1">
          {(client.service_types || []).map((service, idx) => {
            const colors = SERVICE_COLORS[service] || SERVICE_COLORS.Basic;
            return (
              <span 
                key={idx}
                className={`px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
              >
                {service}
              </span>
            );
          })}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-4">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium ${STATUS_COLORS[client.status]?.bg || 'bg-gray-500/20'} ${STATUS_COLORS[client.status]?.text || 'text-gray-400'}`}>
          <StatusIcon className="w-3 h-3" />
          {client.status?.charAt(0).toUpperCase() + client.status?.slice(1)}
        </span>
      </td>

      {/* Current Phase */}
      <td className="px-4 py-4">
        <span className="text-white/70 text-sm">{client.current_phase || 'N/A'}</span>
        {client.build_progress && (
          <div className="mt-1 w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500" 
              style={{ width: `${client.build_progress.percent_complete || 0}%` }}
            />
          </div>
        )}
      </td>

      {/* Revenue */}
      <td className="px-4 py-4">
        <span className="text-white font-medium">{formatCurrency(client.revenue)}</span>
      </td>

      {/* Last Activity */}
      <td className="px-4 py-4">
        <span className="text-white/50 text-sm">{formatDate(client.last_activity)}</span>
        {client.days_active > 0 && (
          <p className="text-white/30 text-xs">{client.days_active} days active</p>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-4 text-right">
        <button 
          className="p-2 text-white/40 hover:text-[#D4AF37] transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          <Eye className="w-5 h-5" />
        </button>
      </td>
    </tr>
  );
};

// Create Client Modal
const CreateClientModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'application'
  const [pendingApps, setPendingApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    business_name: '',
    email: '',
    phone: '',
    mc_number: '',
    dot_number: '',
    has_build: false,
    build_tier: '',
    has_protect: false,
    protect_tier: '',
    has_grow: true,
    grow_tier: 'bronze',
    notes: ''
  });

  useEffect(() => {
    if (activeTab === 'application') {
      fetchPendingApplications();
    }
  }, [activeTab]);

  const fetchPendingApplications = async () => {
    setLoadingApps(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/pending-applications`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingApps(data.applications || []);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoadingApps(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create client');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveApplication = async (appId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/from-application/${appId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create client from application');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Add New Client</h2>
            <p className="text-white/50 text-sm">Create from application or enter manually</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'manual' 
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                : 'text-white/50 hover:text-white'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Manual Entry
          </button>
          <button
            onClick={() => setActiveTab('application')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'application' 
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                : 'text-white/50 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            From Application
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/40 text-red-400 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'manual' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div>
                <h3 className="text-white font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name *"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name *"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Business Name *"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone *"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="MC Number"
                    value={formData.mc_number}
                    onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="DOT Number"
                    value={formData.dot_number}
                    onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                  />
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-white font-semibold mb-3">Services</h3>
                <div className="space-y-4">
                  {/* Build */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_build}
                        onChange={(e) => setFormData({ ...formData, has_build: e.target.checked })}
                        className="w-4 h-4 accent-purple-500"
                      />
                      <Layers className="w-4 h-4 text-purple-400" />
                      <span className="text-white">Build</span>
                    </label>
                    {formData.has_build && (
                      <select
                        value={formData.build_tier}
                        onChange={(e) => setFormData({ ...formData, build_tier: e.target.value })}
                        className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 text-white text-sm"
                      >
                        <option value="">Select Tier</option>
                        <option value="foundation">Foundation ($997)</option>
                        <option value="accelerator">Accelerator ($1,997)</option>
                        <option value="elite">Elite ($2,997)</option>
                      </select>
                    )}
                  </div>

                  {/* Protect */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_protect}
                        onChange={(e) => setFormData({ ...formData, has_protect: e.target.checked })}
                        className="w-4 h-4 accent-green-500"
                      />
                      <Shield className="w-4 h-4 text-green-400" />
                      <span className="text-white">Protect</span>
                    </label>
                    {formData.has_protect && (
                      <select
                        value={formData.protect_tier}
                        onChange={(e) => setFormData({ ...formData, protect_tier: e.target.value })}
                        className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 text-white text-sm"
                      >
                        <option value="">Select Tier</option>
                        <option value="basic">Basic ($197/mo)</option>
                        <option value="professional">Professional ($297/mo)</option>
                        <option value="enterprise">Enterprise ($397/mo)</option>
                      </select>
                    )}
                  </div>

                  {/* Grow */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_grow}
                        onChange={(e) => setFormData({ ...formData, has_grow: e.target.checked })}
                        className="w-4 h-4 accent-orange-500"
                      />
                      <TrendingUp className="w-4 h-4 text-orange-400" />
                      <span className="text-white">Grow</span>
                    </label>
                    {formData.has_grow && (
                      <select
                        value={formData.grow_tier}
                        onChange={(e) => setFormData({ ...formData, grow_tier: e.target.value })}
                        className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 text-white text-sm"
                      >
                        <option value="bronze">Bronze ($300/week)</option>
                        <option value="silver">Silver ($400/week)</option>
                        <option value="gold">Gold ($500/week)</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-white font-semibold mb-3">Notes (Optional)</h3>
                <textarea
                  placeholder="Internal notes about this client..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]/50 focus:outline-none resize-none h-24"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-semibold hover:bg-[#C4A030] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Create Client
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {loadingApps ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
                </div>
              ) : pendingApps.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No pending applications</p>
                </div>
              ) : (
                pendingApps.map((app) => (
                  <div key={app.id} className="bg-white/5 border border-white/10 p-4 flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">{app.company_name || `${app.first_name} ${app.last_name}`}</p>
                      <p className="text-white/50 text-sm">{app.email}</p>
                      <div className="flex gap-2 mt-2">
                        {app.service_type && (
                          <span className={`px-2 py-0.5 text-xs ${SERVICE_COLORS[app.service_type.charAt(0).toUpperCase() + app.service_type.slice(1)]?.bg || 'bg-gray-500/20'} ${SERVICE_COLORS[app.service_type.charAt(0).toUpperCase() + app.service_type.slice(1)]?.text || 'text-gray-400'}`}>
                            {app.service_type}
                          </span>
                        )}
                        <span className="text-white/30 text-xs">
                          Applied {new Date(app.submitted_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleApproveApplication(app.id)}
                      disabled={loading}
                      className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/40 hover:bg-green-500/30 transition-colors flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve & Create
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Client Detail Modal
const ClientDetailModal = ({ client, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientProfile();
  }, [client.id]);

  const fetchClientProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/${client.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'services', label: 'Services', icon: Package },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'metrics', label: 'Metrics', icon: BarChart3 }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-xl">
                {(client.business_name || client.first_name || '?')[0].toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{client.business_name || `${client.first_name} ${client.last_name}`}</h2>
                <p className="text-white/50">{client.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium ${STATUS_COLORS[client.status]?.bg || 'bg-gray-500/20'} ${STATUS_COLORS[client.status]?.text || 'text-gray-400'}`}>
                {client.status?.charAt(0).toUpperCase() + client.status?.slice(1)}
              </span>
              <button onClick={onClose} className="text-white/40 hover:text-white">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab profile={profile} client={client} formatDate={formatDate} />}
              {activeTab === 'services' && <ServicesTab profile={profile} />}
              {activeTab === 'documents' && <DocumentsTab profile={profile} />}
              {activeTab === 'activity' && <ActivityTab profile={profile} formatDate={formatDate} />}
              {activeTab === 'metrics' && <MetricsTab profile={profile} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ profile, client, formatDate }) => {
  const overview = profile?.overview || {};
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Contact Info */}
      <div className="bg-white/5 border border-white/10 p-4">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Building className="w-4 h-4 text-[#D4AF37]" />
          Contact Information
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">Full Name</span>
            <span className="text-white">{overview.first_name} {overview.last_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Email</span>
            <span className="text-white">{overview.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Phone</span>
            <span className="text-white">{overview.phone || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Business</span>
            <span className="text-white">{overview.business_name || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Authority Info */}
      <div className="bg-white/5 border border-white/10 p-4">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#D4AF37]" />
          Authority Information
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">MC Number</span>
            <span className="text-white">{overview.mc_number || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">DOT Number</span>
            <span className="text-white">{overview.dot_number || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Authority Status</span>
            <span className="text-white capitalize">{overview.authority_status || 'Pending'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Hive Access</span>
            <span className={overview.hive_access ? 'text-green-400' : 'text-red-400'}>
              {overview.hive_access ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white/5 border border-white/10 p-4">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#D4AF37]" />
          Account Timeline
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">Start Date</span>
            <span className="text-white">{formatDate(overview.start_date)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Days Active</span>
            <span className="text-white">{overview.days_active || 0} days</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Last Login</span>
            <span className="text-white">{formatDate(overview.last_login)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Account Status</span>
            <span className="text-white capitalize">{overview.account_status || 'Pending'}</span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white/5 border border-white/10 p-4">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
          Quick Stats
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{client.loads_completed || 0}</p>
            <p className="text-white/50 text-xs">Loads Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">{client.compliance_score || 100}%</p>
            <p className="text-white/50 text-xs">Compliance Score</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Services Tab
const ServicesTab = ({ profile }) => {
  const services = profile?.services || {};
  
  const ServiceCard = ({ service, name, icon: Icon, color }) => {
    const isActive = service?.active;
    
    return (
      <div className={`bg-white/5 border ${isActive ? 'border-white/20' : 'border-white/5'} p-4`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 ${isActive ? `bg-${color}-500/20` : 'bg-white/5'} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${isActive ? `text-${color}-400` : 'text-white/20'}`} />
          </div>
          <div>
            <h3 className="text-white font-semibold">{name}</h3>
            <span className={`text-xs ${isActive ? 'text-green-400' : 'text-white/30'}`}>
              {isActive ? 'Active' : 'Not Subscribed'}
            </span>
          </div>
        </div>
        
        {isActive && (
          <div className="space-y-2 text-sm">
            {service.tier && (
              <div className="flex justify-between">
                <span className="text-white/50">Tier</span>
                <span className="text-white capitalize">{service.tier}</span>
              </div>
            )}
            {service.status && (
              <div className="flex justify-between">
                <span className="text-white/50">Status</span>
                <span className="text-white capitalize">{service.status}</span>
              </div>
            )}
            {service.percent_complete !== undefined && (
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/50">Progress</span>
                  <span className="text-white">{service.percent_complete}%</span>
                </div>
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500" 
                    style={{ width: `${service.percent_complete || 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ServiceCard service={services.build} name="Build" icon={Layers} color="purple" />
      <ServiceCard service={services.protect} name="Protect" icon={Shield} color="green" />
      <ServiceCard service={services.grow} name="Grow" icon={TrendingUp} color="orange" />
    </div>
  );
};

// Documents Tab
const DocumentsTab = ({ profile }) => {
  const vault = profile?.document_vault || {};
  const folders = vault.folders || {};
  
  const FolderCard = ({ name, docs, icon: Icon, color }) => (
    <div className="bg-white/5 border border-white/10 p-4">
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-5 h-5 text-${color}-400`} />
        <h3 className="text-white font-semibold capitalize">{name}</h3>
        <span className="text-white/30 text-sm ml-auto">{docs?.length || 0} files</span>
      </div>
      {docs && docs.length > 0 ? (
        <div className="space-y-2">
          {docs.slice(0, 3).map((doc, idx) => (
            <div key={idx} className="text-sm text-white/70 truncate">
              {doc.name || doc.file_name || 'Untitled Document'}
            </div>
          ))}
          {docs.length > 3 && (
            <p className="text-white/30 text-xs">+{docs.length - 3} more</p>
          )}
        </div>
      ) : (
        <p className="text-white/30 text-sm">No documents</p>
      )}
    </div>
  );
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">Document Vault</h3>
        <span className="text-white/50 text-sm">{vault.total_documents || 0} total documents</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FolderCard name="Build" docs={folders.build} icon={Layers} color="purple" />
        <FolderCard name="Compliance" docs={folders.compliance} icon={Shield} color="green" />
        <FolderCard name="Dispatch" docs={folders.dispatch} icon={TrendingUp} color="orange" />
        <FolderCard name="Invoices" docs={folders.invoices} icon={FileText} color="blue" />
      </div>
    </div>
  );
};

// Activity Tab
const ActivityTab = ({ profile, formatDate }) => {
  const activities = profile?.activity_log || [];
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'account_created': return CheckCircle;
      case 'profile_updated': return Edit;
      case 'document_uploaded': return FileText;
      case 'payment_received': return DollarSign;
      default: return Activity;
    }
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-white font-semibold">Activity Log</h3>
      
      {activities.length === 0 ? (
        <div className="text-center py-12">
          <Activity className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No activity recorded yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, idx) => {
            const Icon = getActivityIcon(activity.type);
            return (
              <div key={idx} className="flex items-start gap-3 bg-white/5 border border-white/10 p-3">
                <Icon className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <div className="flex-1">
                  <p className="text-white text-sm">{activity.description}</p>
                  <p className="text-white/30 text-xs mt-1">
                    {formatDate(activity.timestamp)} • by {activity.created_by || 'System'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Metrics Tab
const MetricsTab = ({ profile }) => {
  const metrics = profile?.metrics || {};
  
  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-white font-semibold">Performance Metrics</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-[#D4AF37]">{formatCurrency(metrics.total_revenue)}</p>
          <p className="text-white/50 text-sm mt-1">Total Revenue</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-white">{metrics.loads_completed || 0}</p>
          <p className="text-white/50 text-sm mt-1">Loads Completed</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-green-400">{metrics.compliance_score || 100}%</p>
          <p className="text-white/50 text-sm mt-1">Compliance Score</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-purple-400">{metrics.build_progress || 0}%</p>
          <p className="text-white/50 text-sm mt-1">Build Progress</p>
        </div>
      </div>
      
      {/* Invoice Summary */}
      <div className="bg-white/5 border border-white/10 p-4">
        <h4 className="text-white font-semibold mb-4">Invoice Summary</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xl font-bold text-white">{formatCurrency(metrics.invoices_total)}</p>
            <p className="text-white/50 text-xs">Total Invoiced</p>
          </div>
          <div>
            <p className="text-xl font-bold text-green-400">{formatCurrency(metrics.invoices_paid)}</p>
            <p className="text-white/50 text-xs">Paid</p>
          </div>
          <div>
            <p className="text-xl font-bold text-yellow-400">{formatCurrency(metrics.invoices_pending)}</p>
            <p className="text-white/50 text-xs">Pending</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClientProfilesSection;
